import {
  LoanCustomPaymentIntention,
  LoanCustomPaymentOption,
} from '@possible/cassandra/src/types/types.mobile.generated'
import {AppEvents, ManageActiveLoanEvents} from 'src/lib/Analytics/app_events'
import i18n from 'src/lib/localization/i18n'
import Log from 'src/lib/loggingUtil'
import {formatMaskNonBreaking} from 'src/lib/user/utils'
import {
  LoanPaymentMethod,
  LoanPaymentMethodAch,
  LoanPaymentMethodDebitCard,
} from 'src/products/loans/MakePayment/LoanPay.types'
import {TrackAppEvent} from 'src/lib/Analytics/analytics_compat'
import {LoanCustomPaymentOptionWithMembershipOverdueAmount} from 'src/products/loans/MakePayment/LoanPayAmountEntry/LoanPayAmountEntry.types'

type LoanCustomPaymentIntentionWithMembershipOverdueAmount =
  | LoanCustomPaymentIntention
  | 'MEMBERSHIP_OVERDUE_AMOUNT'

/**
 * A map of LoanCustomPaymentIntention values to their corresponding display labels.
 */
type MapOfLoanPaymentOptionIntention = {
  [key in LoanCustomPaymentIntentionWithMembershipOverdueAmount]: string
}

/**
 * A map of LoanCustomPaymentIntention values to their corresponding display labels.
 */
const loanPaymentOptionIntentionMap: MapOfLoanPaymentOptionIntention = {
  [LoanCustomPaymentIntention.FullBalance]: i18n.t('MakePaymentAmountEntry:FullBalance'),
  [LoanCustomPaymentIntention.MissedInstallment]: i18n.t(
    'MakePaymentAmountEntry:MissedInstallment',
  ),
  [LoanCustomPaymentIntention.NextInstallment]: i18n.t('MakePaymentAmountEntry:NextInstallment'),
  [LoanCustomPaymentIntention.OverdueBalance]: i18n.t('MakePaymentAmountEntry:OverdueBalance'),
  [LoanCustomPaymentIntention.OtherAmount]: i18n.t('MakePaymentAmountEntry:OtherAmount'),
  MEMBERSHIP_OVERDUE_AMOUNT: i18n.t('MakePaymentAmountEntry:MembershipOverdueAmount'),
}

/**
 * Get the display label for a LoanCustomPaymentOption.
 */
export const getLoanPaymentOptionLabel = (
  paymentOption: LoanCustomPaymentOptionWithMembershipOverdueAmount,
): string => {
  return loanPaymentOptionIntentionMap[paymentOption.intention] ?? ''
}

/**
 * Get the trackingId for a LoanCustomPaymentOption that is used as an ID to reference individual payment options.
 */
export const getLoanPaymentOptionTrackingId = (paymentOption: LoanCustomPaymentOption): string => {
  return paymentOption.intention
}

const addLoanMakePaymentIdentifierToError = (e: Error): Error => {
  const modifiedError = new Error(`${e.message} - Loan Make Payment`)
  modifiedError.stack = e.stack
  return modifiedError
}

/**
 * Log errors related to Loans Make Payment. Includes standardized log prefix.
 */
export const loanMakePaymentLogError = (e: Error, msg?: string): void => {
  Log.error(addLoanMakePaymentIdentifierToError(e), msg)
}

export const convertPaymentIntentionStringToEnum = (
  intentionString: string,
): LoanCustomPaymentIntentionWithMembershipOverdueAmount | undefined => {
  const isLoanCustomPaymentIntention = (
    str: string,
  ): str is LoanCustomPaymentIntentionWithMembershipOverdueAmount => {
    return (
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      Object.values(LoanCustomPaymentIntention).includes(str as LoanCustomPaymentIntention) ||
      str === 'MEMBERSHIP_OVERDUE_AMOUNT'
    )
  }

  if (isLoanCustomPaymentIntention(intentionString)) {
    let intention: LoanCustomPaymentIntentionWithMembershipOverdueAmount
    // action.trackingId is a string, we need to convert it back to the correct enum value using
    // a type guard
    switch (intentionString) {
      case LoanCustomPaymentIntention.FullBalance:
        intention = LoanCustomPaymentIntention.FullBalance
        break
      case LoanCustomPaymentIntention.MissedInstallment:
        intention = LoanCustomPaymentIntention.MissedInstallment
        break
      case LoanCustomPaymentIntention.NextInstallment:
        intention = LoanCustomPaymentIntention.NextInstallment
        break
      case LoanCustomPaymentIntention.OverdueBalance:
        intention = LoanCustomPaymentIntention.OverdueBalance
        break
      case LoanCustomPaymentIntention.OtherAmount:
        intention = LoanCustomPaymentIntention.OtherAmount
        break
      case 'MEMBERSHIP_OVERDUE_AMOUNT':
        intention = 'MEMBERSHIP_OVERDUE_AMOUNT'
        break
    }
    return intention
  }
}

/**
 * Tracks when any loan make payment screen fails to render and we show an error UX instead.
 */
export const trackLoansMakePaymentScreenRenderFailure = (e: Error, routeName: string): void => {
  TrackAppEvent(
    ManageActiveLoanEvents.loans_adhoc_payment_error_screen_viewed,
    AppEvents.Category.ManageActiveLoan,
    {
      route: routeName,
    },
  )
  loanMakePaymentLogError(e, `${routeName} render failed and threw`)
}

export const isAchPaymentMethod = (
  paymentMethod: LoanPaymentMethod,
): paymentMethod is LoanPaymentMethodAch => {
  return (
    typeof paymentMethod === 'object' &&
    paymentMethod !== null &&
    '__typename' in paymentMethod &&
    paymentMethod.__typename === 'AchPaymentMethod'
  )
}

export const isDebitCardPaymentMethod = (
  paymentMethod: LoanPaymentMethod,
): paymentMethod is LoanPaymentMethodDebitCard => {
  return (
    typeof paymentMethod === 'object' &&
    paymentMethod !== null &&
    '__typename' in paymentMethod &&
    paymentMethod.__typename === 'DebitCardPaymentMethod'
  )
}

export const getPaymentMethodFriendlyName = (paymentMethod: LoanPaymentMethod): string | null => {
  if (isAchPaymentMethod(paymentMethod)) {
    return paymentMethod.bankAccount.friendlyName ?? null
  }
  if (isDebitCardPaymentMethod(paymentMethod)) {
    return paymentMethod.card.friendlyName ?? null
  }
  return null
}

export const getPaymentMethodMask = (paymentMethod: LoanPaymentMethod): string | null => {
  if (isAchPaymentMethod(paymentMethod)) {
    return paymentMethod.bankAccount.mask ?? null
  }
  if (isDebitCardPaymentMethod(paymentMethod)) {
    return paymentMethod.card.mask ?? null
  }
  return null
}

export const getPaymentMethodLabel = (paymentMethod: LoanPaymentMethod): string => {
  const friendlyName = getPaymentMethodFriendlyName(paymentMethod) ?? ''
  const accountMask = getPaymentMethodMask(paymentMethod) ?? ''
  const formattedMask = formatMaskNonBreaking(accountMask, false)
  const cardEnding = i18n.t('PaymentMethods:CardEndingIn', {card: accountMask})

  if (isAchPaymentMethod(paymentMethod)) {
    return `${friendlyName} (${formattedMask})`
  }
  if (isDebitCardPaymentMethod(paymentMethod)) {
    return `${friendlyName}\n${cardEnding}`
  }
  return ''
}
